/**
 * Map a template-extracted {@link ScrubbedForm} (from form-parser.ts running the
 * JURY_FORM_TEMPLATE) into a {@link Juror} the scoring engine understands.
 *
 * The template already enforced the privacy contract at the pixel level (only the
 * curated boxes were ever cropped/recognized — name/address/employer were never
 * read). This module's job is purely CANONICALIZATION: turn handwritten text and
 * marked-checkbox arrays into the exact option strings core/scoring.ts keys on
 * (e.g. 'Bachelor degree', 'Retired', 'Yes – crime victim'). The canonical
 * vocabulary is kept identical to core/privacy.ts so both OCR paths (template
 * TrOCR and Tesseract fallback) yield interchangeable jurors.
 */
import type { Juror } from '@/types';
import type { ScrubbedForm } from '@/types/form';
import { scrubFreeText } from '@/core/privacy';

/** Free-text "describe" boxes: the juror wrote these, so scrub PII before storing. */
const DESCRIBE_FIELDS = [
  'experience_with_law_describe',
  'law_related_employment_describe',
  'impartiality_describe'
];

/** Read a scrubbed text/number field as a trimmed lowercase string. */
function text(f: ScrubbedForm, id: string): string {
  const v = f[id];
  return typeof v === 'string' ? v.trim() : typeof v === 'number' ? String(v) : '';
}

/** Read a scrubbed checkbox group as its marked values. */
function marks(f: ScrubbedForm, id: string): string[] {
  const v = f[id];
  return Array.isArray(v) ? v : [];
}

/** Bucket an exact age into the questionnaire's AgeBucket labels. */
function ageBucket(n: number): string {
  return n < 25 ? '18-24'
    : n < 35 ? '25-34'
    : n < 45 ? '35-44'
    : n < 55 ? '45-54'
    : n < 65 ? '55-64'
    : n < 75 ? '65-74'
    : '75+';
}

/** Canonicalize free-text education to a scoring option string (raw if unknown). */
function canonEducation(raw: string): string {
  const t = raw.toLowerCase();
  if (!t) return '';
  if (/phd|doctorate|doctoral/.test(t)) return 'PhD / Doctoral';
  if (/j\.?d\.?|law degree|juris/.test(t)) return 'JD / Law degree';
  if (/m\.?d\.?|medical degree/.test(t)) return 'MD / Medical';
  if (/master|m\.?s\.?|m\.?a\.?/.test(t)) return 'Master degree';
  if (/bachelor|b\.?s\.?|b\.?a\.?|4.year college|four.year/.test(t)) return 'Bachelor degree';
  if (/associate/.test(t)) return 'Associate degree';
  if (/some college|technical|trade school|vocational/.test(t)) return 'Some college / trade school';
  if (/high school|hs diploma|ged|graduated high/.test(t)) return 'HS diploma / GED';
  if (/less than high|did not.*graduate/.test(t)) return 'Less than high school';
  return raw.trim(); // keep what was written so the reviewer can correct it
}

/** Canonicalize free-text business/industry to a scoring option string. */
function canonBizType(raw: string): string {
  const t = raw.toLowerCase();
  if (!t) return '';
  if (/hospital|clinic|medical|health care|healthcare|nurse/.test(t)) return 'Healthcare / Medical';
  if (/school|university|college|academic|educat/.test(t)) return 'Education / Academic';
  if (/law firm|attorney|legal|court|judicial/.test(t)) return 'Legal / Law';
  if (/police|law enforce|sheriff|correction|probation|parole/.test(t)) return 'Law Enforcement / Corrections';
  if (/military|army|navy|marines|air force|national guard/.test(t)) return 'Military / Defense';
  if (/government|federal|state.*employ|municipal|public sector/.test(t)) return 'Government / Public sector';
  if (/bank|financ|insurance|invest|accounting/.test(t)) return 'Finance / Banking / Insurance';
  if (/technolog|software|computer|it |information tech/.test(t)) return 'Technology / IT';
  if (/construct|trade|plumb|electric|mechanic|carpent/.test(t)) return 'Trades / Construction / Labor';
  if (/manufactur|industrial|factor/.test(t)) return 'Manufacturing / Industrial';
  if (/retail|sales|store|shop/.test(t)) return 'Retail / Sales';
  if (/restaurant|food|hospitality|hotel|catering/.test(t)) return 'Food / Hospitality';
  if (/transport|logistics|trucking|deliver/.test(t)) return 'Transportation / Logistics';
  if (/media|communicat|journal|publish|arts|entertain/.test(t)) return 'Media / Communications / Arts';
  if (/social service|nonprofit|non.profit|ngo/.test(t)) return 'Social Services / Nonprofit';
  if (/church|religious|clergy|pastor|minister|rabbi|imam/.test(t)) return 'Religious / Clergy';
  return raw.trim();
}

/** Map the work/school checkbox group to a single empStatus (priority-ordered). */
function empStatusFrom(m: string[]): string {
  if (m.includes('self_employed')) return 'Self-employed';
  if (m.includes('retired')) return 'Retired';
  if (m.includes('student')) return 'Student';
  if (m.includes('at_home')) return 'Homemaker';
  if (m.includes('unemployed')) return 'Unemployed / seeking';
  if (m.includes('part_time')) return 'Employed part-time';
  if (m.includes('employed')) return 'Employed full-time';
  if (m.includes('other')) return 'Other';
  return '';
}

/** Map the law-related-employment checkbox group to a lawEmp label. */
function lawEmpFrom(m: string[]): string {
  if (m.includes('law_enforcement')) return 'Law enforcement agency';
  if (m.includes('court_system')) return 'Court officer / Clerk';
  if (m.includes('corrections')) return 'Corrections officer';
  if (m.includes('other_law_related')) return 'Other law-related';
  return 'None';
}

/** Convert one parsed questionnaire into a Partial<Juror> for the review card. */
export function scrubbedToJuror(f: ScrubbedForm, panelNumber: number): Partial<Juror> {
  const j: Partial<Juror> = {};
  j.panelId = 'J-' + String(panelNumber).padStart(2, '0');

  // Gender / sex identity
  const sex = text(f, 'sex_gender_identity').toLowerCase();
  if (/female|woman/.test(sex)) j.gender = 'Female';
  else if (/non.?binary|genderqueer|gender.?fluid/.test(sex)) j.gender = 'Non-binary';
  else if (/male|man/.test(sex)) j.gender = 'Male';
  else if (sex) j.gender = text(f, 'sex_gender_identity');

  // Age — store exact numeric string (scoring's jurorAge parseInts it)
  const ageN = parseInt(text(f, 'age'), 10);
  if (!isNaN(ageN) && ageN >= 18 && ageN < 100) j.age = String(ageN);

  // Education / business / occupation
  j.edu = canonEducation(text(f, 'education_highest_grade'));
  j.bizType = canonBizType(text(f, 'type_of_business'));
  j.occ = text(f, 'job_occupation');

  // Employment status (checkboxes)
  j.empStatus = empStatusFrom(marks(f, 'work_school_current_past'));

  // Parent / guardian
  const pg = marks(f, 'parent_guardian_of_children');
  if (pg.includes('yes')) j.parent = 'Yes – minor children';
  else if (pg.includes('no')) j.parent = 'No';

  // Spouse / partner (age + education kept; name & employer never captured)
  const spAge = parseInt(text(f, 'partner_age'), 10);
  if (!isNaN(spAge) && spAge >= 18 && spAge < 100) j.spouseAge = ageBucket(spAge);
  const spEdu = canonEducation(text(f, 'spouse_partner_education'));
  if (spEdu) j.spouseEdu = spEdu;

  // Experience with the law (checkbox group)
  const exp = marks(f, 'experience_with_law');
  j.victim = exp.includes('been_crime_victim') ? 'Yes – crime victim' : 'No';
  j.accused = (exp.includes('been_arrested') || exp.includes('been_charged') || exp.includes('been_convicted'))
    ? 'Yes – self or family charged/convicted' : 'No';
  j.jury = exp.includes('been_seated_juror') ? 'Served, type unknown' : 'No';

  // Law-related employment → lawEmp, and LE connection if it's law enforcement
  const lawEmp = marks(f, 'law_related_employment');
  j.lawEmp = lawEmpFrom(lawEmp);
  j.le = lawEmp.includes('law_enforcement') ? 'Household / family – law enforcement' : 'None';

  // Impartiality — "no" means nothing affects fairness; "yes" flags a concern
  const imp = marks(f, 'impartiality');
  if (imp.includes('yes')) j.fair = 'Uncertain – stated concern';
  else if (imp.includes('no')) j.fair = 'Yes – stated can be fair';

  // Not asked on this form
  j.caseKnow = 'None stated';
  j.opinion = 'None stated';

  // Persist the FULL extracted field map so nothing captured is lost — including
  // fields with no dedicated Juror column (spouse business/occupation, the
  // describe boxes, qualified/gate answers). The free-text describe boxes are
  // scrubbed of any PII the juror may have written in them before storage.
  const persisted: ScrubbedForm = { ...f };
  for (const id of DESCRIBE_FIELDS) {
    const v = persisted[id];
    if (typeof v === 'string' && v) persisted[id] = scrubFreeText(v);
  }
  j.questionnaire = persisted;
  return j;
}

export default scrubbedToJuror;
